import { computed, ref } from 'vue';
import { useGlobal, useMap } from '@/composables';
import type { FirstAnimationFrame, Markets, Poi } from '@/types';

function usePoisSingleton() {
  const pois = ref<Poi[]>([
    {
      name: 'element-0',
      category: 'construction',
      alt: 'construction site',
      width: 671,
      height: 495,
      top: 1095,
      left: 2686,
      subpois: [1],
      pin: {
        top: 1241,
        left: 3067,
      },
    },
    {
      name: 'element-1',
      category: 'civil-engineering',
      alt: 'road construction',
      width: 715,
      height: 533,
      top: 298,
      left: 84,
      subpois: [0, 1, 1, 1, 1],
      pin: {
        top: 614,
        left: 530,
      },
    },
    {
      name: 'element-2',
      category: 'construction',
      alt: 'house construction',
      width: 338,
      height: 176,
      top: 1977,
      left: 2311,
      subpois: [1],
      pin: {
        top: 2046,
        left: 2498,
      },
    },
    {
      name: 'element-group-3-4',
      category: 'agriculture',
      alt: 'agriculture',
      width: 1149,
      height: 901,
      top: 1719,
      left: 0,
      subpois: [0, 1, 2],
      pin: {
        top: 2004,
        left: 559,
      },
    },
    {
      name: 'element-7',
      category: 'water',
      alt: 'purification station',
      width: 1286,
      height: 453,
      top: 2439,
      left: 568,
      subpois: [1],
      pin: {
        top: 2560,
        left: 1264,
      },
    },
    {
      name: 'element-8',
      category: 'non-ferrous',
      alt: 'electric car',
      width: 255,
      height: 118,
      top: 1492,
      left: 1703,
      subpois: [1],
      pin: {
        top: 1536,
        left: 1753,
      },
    },
    {
      name: 'element-9',
      category: 'flue-gas-treatment',
      alt: 'factory',
      width: 622,
      height: 362,
      top: 1978,
      left: 1322,
      subpois: [0, 1, 1],
      pin: {
        top: 2121,
        left: 1587,
      },
    },
    {
      name: 'element-10',
      category: 'glass',
      alt: 'glass building',
      width: 247,
      height: 505,
      top: 270,
      left: 3337,
      subpois: [1],
      pin: {
        top: 330,
        left: 3491,
      },
    },
    {
      name: 'element-11',
      category: 'lhoist',
      alt: 'quarry',
      width: 1629,
      height: 1015,
      top: 256,
      left: 969,
      subpois: [1],
      pin: {
        top: 536,
        left: 1715,
      },
    },
    {
      name: 'element-12',
      category: 'lhoist',
      alt: 'lab',
      width: 687,
      height: 446,
      top: 1154,
      left: 436,
      subpois: [1],
      pin: {
        top: 1224,
        left: 711,
      },
    },
    {
      name: 'element-13',
      category: 'lhoist',
      alt: 'office',
      width: 471,
      height: 641,
      top: 1100,
      left: 3266,
      subpois: [0, 1, 1, 1],
      pin: {
        top: 1168,
        left: 3479,
      },
    },
    {
      name: 'element-14',
      category: 'agriculture',
      alt: 'supermarket',
      width: 507,
      height: 309,
      top: 1336,
      left: 1877,
      subpois: [0],
      pin: {
        top: 1419,
        left: 2137,
      },
    },
    {
      name: 'element-15',
      category: 'lhoist',
      alt: 'university',
      width: 508,
      height: 364,
      top: 1148,
      left: 3818,
      subpois: [3],
      pin: {
        top: 1145,
        left: 4057,
      },
    },
    {
      name: 'element-16',
      category: 'paper',
      alt: 'bookstore',
      width: 463,
      height: 250,
      top: 1530,
      left: 3865,
      subpois: [1],
      pin: {
        top: 1557,
        left: 4046,
      },
    },
    {
      name: 'element-17',
      category: 'steel',
      alt: 'house and car',
      width: 144,
      height: 120,
      top: 2364,
      left: 2271,
      subpois: [1],
      pin: {
        top: 2384,
        left: 2322,
      },
    },
  ]);

  const { isHighQuality } = useMap();
  const { isPoiFocused } = useGlobal();

  const selectedCategory = ref<Markets[number] | null>(null);
  const selectedPoi = ref<Poi | null>(null);
  const selectedSubpoiIndex = ref(0);

  const poiToFirstsMapper = ref<Map<Poi['name'], FirstAnimationFrame<'rgb'>[]>>(
    new Map([
      ['element-0', ['anim-id-2-A-first-rgb']],
      ['element-1', ['anim-id-33-A-first-rgb', 'anim-id-34-A-first-rgb']],
      ['element-group-3-4', ['anim-id-6-A-first-rgb', 'anim-id-32-A-first-rgb']],
      ['element-11', ['anim-id-3-A-first-rgb', 'anim-id-36-A-first-rgb', 'anim-id-37-A-first-rgb']],
    ]),
  );

  const poiFirstsFrames = computed(() => {
    const result: FirstAnimationFrame<'rgb'>[] = [];
    filteredPois.value.forEach((poi) => {
      const firsts = poiToFirstsMapper.value.get(poi.name) ?? [];
      result.push(...firsts);
    });
    return result;
  });

  const filteredPois = computed(() => {
    if (selectedPoi.value) {
      return [selectedPoi.value];
    }
    return !selectedCategory.value
      ? pois.value
      : pois.value.filter((poi) => poi.category === selectedCategory.value);
  });

  const selectedPoiSubpoisLength = computed(() =>
    selectedPoi.value?.subpois ? selectedPoi.value.subpois.length - 1 : 0,
  );
  const isPoiAmongFiltered = computed(
    () => (name: Poi['name']) => !!filteredPois.value.find((poi) => poi.name === name),
  );

  const isPoiDisplayed = computed(
    () => (name: Poi['name']) => isPoiAmongFiltered.value(name) && !isPoiFocused.value,
  );
  const isPoiHighQuality = computed(() => isHighQuality.value || !!selectedPoi.value);

  const selectPoi = (name: Poi['name']) => {
    selectedPoi.value = pois.value.find((poi) => poi.name === name) ?? null;
  };

  return {
    pois,
    filteredPois,
    poiFirstsFrames,
    selectedPoi,
    selectPoi,
    selectedSubpoiIndex,
    selectedPoiSubpoisLength,
    selectedCategory,
    isPoiAmongFiltered,
    isPoiDisplayed,
    isPoiHighQuality,
  };
}

const cached = usePoisSingleton();
export const usePois = () => cached;
